import {
  ORDER_STATUS_LABELS,
  ORDER_STATUS_COLORS,
  STATUS_DATE_LABEL,
  formatBusinessDateShort,
  formatBusinessDateTimeShort,
  formatDeliveryDate,
  orderStatusDate,
  orderTitle,
} from "@/lib/orderUtils";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useDraggable } from "@dnd-kit/core";
import {
  AlertTriangle,
  ArrowRight,
  CalendarClock,
  CalendarDays,
  Eye,
  GripVertical,
  MoreVertical,
  Package,
  RotateCcw,
  Store,
  XCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { canSeeOrderPricing, type OrderStatus, type OrderSummary } from "@/lib/types";
import { useApp } from "@/store/useApp";

interface Props {
  order: OrderSummary;
  onOpen: (orderId: string) => void;
  onAdvance?: (order: OrderSummary) => void;
  onCancel?: (order: OrderSummary) => void;
  onEditDates?: (order: OrderSummary) => void;
  onRestore?: (order: OrderSummary) => void;
  onScheduleProduction?: (order: OrderSummary) => void;
  nextStatus?: OrderStatus | null;
  draggable?: boolean;
}

const PRODUCTION_SCHEDULABLE: OrderStatus[] = ["CRIADO", "EM_COMPRA"];

const FORWARD_STATUSES: OrderStatus[] = ["CRIADO", "EM_COMPRA", "EM_PRODUCAO", "EM_EXPEDICAO", "EM_ROTA", "ENTREGUE"];

export function OrderCard({
  order,
  onOpen,
  onAdvance,
  onCancel,
  onEditDates,
  onRestore,
  onScheduleProduction,
  nextStatus,
  draggable = false,
}: Props) {
  const canSchedule = Boolean(onScheduleProduction) && PRODUCTION_SCHEDULABLE.includes(order.status);
  const colors = ORDER_STATUS_COLORS[order.status];
  const showPricing = canSeeOrderPricing(useApp((s) => s.account));

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: order.id,
    data: { orderId: order.id, status: order.status },
    disabled: !draggable,
  });

  const STATUSES_WITH_TIME: OrderStatus[] = ["EM_ROTA", "ENTREGUE"];
  const dateEntries = FORWARD_STATUSES.map((status) => {
    const iso = orderStatusDate(order, status);
    const formatted = iso
      ? STATUSES_WITH_TIME.includes(status)
        ? formatBusinessDateTimeShort(iso)
        : formatBusinessDateShort(iso)
      : undefined;
    return { status, label: STATUS_DATE_LABEL[status], formatted };
  });
  if (order.cancelledAt) {
    dateEntries.push({
      status: "CANCELADO",
      label: STATUS_DATE_LABEL.CANCELADO,
      formatted: formatBusinessDateShort(order.cancelledAt),
    });
  }

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      role="button"
      tabIndex={0}
      onClick={() => onOpen(order.id)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen(order.id);
        }
      }}
      className={cn(
        "group relative cursor-pointer rounded-lg border bg-surface p-3 shadow-sm transition-all",
        "hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
        isDragging && "pointer-events-none opacity-40",
      )}
    >
      {draggable && (
        <button
          {...listeners}
          type="button"
          aria-label="Arrastar pedido"
          className="absolute left-0 top-0 flex h-full w-4 cursor-grab touch-none items-center justify-center rounded-l-lg text-muted-foreground/30 opacity-0 transition-opacity hover:bg-muted/50 hover:text-muted-foreground group-hover:opacity-100 active:cursor-grabbing"
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      )}

      {canSchedule && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onScheduleProduction?.(order);
          }}
          className={cn(
            "mb-2 ml-1 flex w-[calc(100%-0.25rem)] items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] font-semibold transition-colors",
            order.scheduledProductionDate
              ? "border-primary/30 bg-primary/10 text-primary hover:bg-primary/15"
              : "border-dashed border-border text-muted-foreground hover:border-primary/50 hover:text-primary",
          )}
        >
          <CalendarClock className="h-3 w-3 shrink-0" />
          {order.scheduledProductionDate ? (
            <span className="truncate">Produção: {formatDeliveryDate(order.scheduledProductionDate)}</span>
          ) : (
            <span>Agendar produção</span>
          )}
        </button>
      )}

      <div className="flex items-start gap-2 pl-1">
        <div className="min-w-0 flex-1">
          {order.fulfillmentMode === "RETIRADA" && (
            <span className="mb-1 inline-flex items-center gap-1 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">
              <Store className="h-3 w-3" /> Retirada na loja
            </span>
          )}
          <span className="block truncate font-display text-sm font-bold leading-tight">
            {orderTitle(order)}
          </span>
          {order.productionAutoAdvancedPending && (
            <span className="mt-1 inline-flex items-center gap-1 rounded bg-destructive/10 px-1.5 py-0.5 text-[10px] font-semibold text-destructive">
              <AlertTriangle className="h-3 w-3" /> Avançou sem compra concluída
            </span>
          )}
          <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
            <Package className="h-3 w-3" />
            <span>
              {order.itemCount} ite{order.itemCount === 1 ? "m" : "ns"}
            </span>
            {showPricing && order.total !== undefined && (
              <>
                <span className="text-muted-foreground/40">·</span>
                <span className="font-semibold tabular-nums text-foreground">
                  R$ {order.total.toFixed(2)}
                </span>
              </>
            )}
          </div>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button variant="ghost" size="icon" className="-mr-1 -mt-1 h-7 w-7 shrink-0">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
            <DropdownMenuItem onClick={() => onOpen(order.id)}>
              <Eye className="h-4 w-4" /> Ver detalhes
            </DropdownMenuItem>
            {onEditDates && (
              <DropdownMenuItem onClick={() => onEditDates(order)}>
                <CalendarDays className="h-4 w-4" /> Editar datas
              </DropdownMenuItem>
            )}
            {onAdvance && nextStatus && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onAdvance(order)}>
                  <ArrowRight className="h-4 w-4" /> Avançar para {ORDER_STATUS_LABELS[nextStatus]}
                </DropdownMenuItem>
              </>
            )}
            {onCancel && order.status !== "CANCELADO" && order.status !== "ENTREGUE" && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onCancel(order)} className="text-destructive focus:text-destructive">
                  <XCircle className="h-4 w-4" /> Cancelar pedido
                </DropdownMenuItem>
              </>
            )}
            {onRestore && order.status === "CANCELADO" && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onRestore(order)}>
                  <RotateCcw className="h-4 w-4" /> Restaurar pedido
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="mt-2 space-y-1 border-t border-border/60 pl-1 pt-2 text-[11px]">
        {dateEntries.map((entry) => (
          <div key={entry.status} className="flex min-w-0 items-baseline justify-between gap-2 text-muted-foreground">
            <span className="shrink-0 font-medium text-foreground/70">{entry.label}:</span>
            <span className={cn("min-w-0 text-right tabular-nums", !entry.formatted && "text-muted-foreground/50")}>
              {entry.formatted ?? "—"}
            </span>
          </div>
        ))}
      </div>

      {(order.deliveryWindow || order.deliveryDate) && (
        <div className="mt-2 border-t border-border/60 pl-1 pt-2">
          <div
            className={cn(
              "flex items-start gap-1.5 rounded-md border px-2 py-1 text-[11px] font-semibold",
              colors.bg,
              colors.text,
              colors.border,
            )}
          >
            <CalendarClock className="mt-0.5 h-3 w-3 shrink-0" />
            <div className="flex flex-col">
              <span className="text-[10px] font-medium uppercase tracking-wide opacity-80">
                Entrega agendada
              </span>
              <span>{order.deliveryWindow ?? order.deliveryDate}</span>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
